import React, { createContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";

const EventContext = createContext({
  event: null,
  loading: false,
  error: null,
});

/**
 * EventContext Provider
 * Fetches event data based on URL pathname
 * Handles /events/:eventSlug routes
 */
export const EventProvider = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchEvent = useCallback(
    async (slug, skipLoading = false) => {
      console.log(
        "[EventContext] fetchEvent called",
        slug,
        "skipLoading:",
        skipLoading
      );
      if (!skipLoading) {
        setLoading(true);
      }
      setError(null);

      try {
        const response = await axios.get(`/api/events/${slug}`);
        console.log("[EventContext] API response received", response.data);
        setEvent(response.data);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          setError("Event not found");
          setTimeout(() => {
            navigate("/");
          }, 1000);
          if (!skipLoading) {
            setEvent(null);
          }
        } else {
          setError("Failed to load event");
          console.error("Error fetching event:", err);
          if (!skipLoading) {
            setEvent(null);
          }
        }
      } finally {
        if (!skipLoading) {
          setLoading(false);
        }
      }
    },
    [navigate]
  );

  // Handle route changes and check for state
  // Only listen to pathname changes, not query params (like CityContext)
  // This prevents unnecessary rerenders when query params change (e.g., keyword searches)
  useEffect(function fetchEventOnRouteChange() {
    const pathname = location.pathname;
    const eventMatch = pathname.match(/^\/events\/([^/]+?)(?:\/embed)?\/?$/);

    console.log("[EventContext] Location check", {
      pathname,
      hasState: !!location.state,
      search: location.search,
      eventMatch: !!eventMatch,
      currentEventId: event?._id,
    });

    console.log("[EventContext] Location changed", {
      pathname,
      hasState: !!location.state,
      state: location.state,
      stateEvent: location.state?.event,
    });

    if (eventMatch) {
      const eventSlug = eventMatch[1];
      const stateEvent = location.state?.event;

      console.log("[EventContext] Event route detected", {
        eventSlug,
        hasStateEvent: !!stateEvent,
        stateEventSlug: stateEvent?.slug,
        slugsMatch: stateEvent?.slug === eventSlug,
      });

      // If we have event data in state and slug matches, use it immediately
      if (stateEvent && stateEvent.slug === eventSlug) {
        console.log(
          "[EventContext] Setting event from state immediately",
          stateEvent
        );
        setEvent(stateEvent);
        setLoading(false);
        setError(null);
        // Fetch from API in background to get fresh data, but don't set loading
        console.log(
          "[EventContext] Fetching event from API (background)",
          eventSlug
        );
        fetchEvent(eventSlug, true);
      } else {
        console.log(
          "[EventContext] No valid state event, will fetch from API"
        );
        // No state data, fetch normally with loading indicator
        console.log("[EventContext] Fetching event from API", eventSlug);
        fetchEvent(eventSlug);
      }
    } else {
      console.log("[EventContext] Not an event route, clearing event");
      setEvent(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.state, fetchEvent]);

  const value = {
    event,
    loading,
    error,
  };

  return (
    <EventContext.Provider value={value}>{children}</EventContext.Provider>
  );
};

export default EventContext;
